// src/pages/ForgotPassword.jsx

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '../api/auth';
import { useSubmissionLock } from '../hooks/useSubmissionLock';

function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { run: runSubmission, isPending } = useSubmissionLock();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || isPending('forgot-password')) return;

    await runSubmission('forgot-password', async () => {
      setLoading(true);
      setError('');
      setSuccess(false);

      const email = e.target.email.value.trim();

      try {
        await forgotPassword({ email });
        setSuccess(true);
      } catch (err) {
        console.error(err);
        setError(err?.response?.data?.error || 'Unable to send reset email.');
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <div className="flex h-screen">
      {/* LEFT SIDE */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-gradient-to-br from-[#111] via-[#1a1a1a] to-[#2b2b2b] p-8">
        <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-10 w-full max-w-md shadow-2xl">
          <div className="text-center mb-10">
            <img
              src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6824d581ae24f69d45e26536_beem%20logo%203.png"
              alt="Beem Logo"
              className="w-24 mx-auto mb-4"
            />

            <h2 className="text-3xl font-bold text-white mb-2">
              Forgot Password
            </h2>

            <p className="text-gray-400 text-sm">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          {success ? (
            <div className="text-center">
              <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6">
                <p className="text-green-400 text-sm">
                  Password reset email sent.
                </p>

                <p className="text-gray-300 text-sm mt-2">
                  Check your inbox and follow the link to create a new password.
                </p>
              </div>

              <Link
                to="/sign-in"
                className="inline-block text-[#AF9B8A] hover:underline"
              >
                Back to Sign In
              </Link>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label
                    htmlFor="email"
                    className="block mb-1 text-sm font-medium text-gray-300"
                  >
                    Email address
                  </label>

                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/15 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
                    placeholder="you@example.com"
                  />
                </div>

                {error && (
                  <p className="text-red-400 text-center text-sm">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-4 bg-[#AF9B8A] hover:bg-[#9e8a78] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <p className="mt-8 text-center text-gray-400 text-sm">
                Remember your password?{' '}
                <Link
                  to="/sign-in"
                  className="text-[#AF9B8A] hover:underline"
                >
                  Sign In
                </Link>
              </p>
            </>
          )}
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div className="hidden md:block w-1/2 relative">
        <img
          src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/68e10ee19b260e7f43fddd20_ChatGPT%20Image%20Oct%204%2C%202025%2C%2012_26_41%20PM.png"
          alt="City illustration"
          className="object-cover w-full h-full"
        />

        <div className="absolute inset-0 bg-gradient-to-l from-black/20 to-transparent" />
      </div>
    </div>
  );
}

export default ForgotPassword;
